import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { productDetails } from "@/mocks/productDetails";
import { featuredProducts } from "@/mocks/products";

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const product = productDetails.find(p => p.id === Number(id));

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState("");
  const [activeTab, setActiveTab] = useState("description");

  if (!product) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6 py-24">
          <i className="ri-error-warning-line text-[3rem] text-[#c9a98a] mb-4"></i>
          <h2 className="text-[#3d2b1f] text-[1.75rem] font-bold mb-3">找不到此商品</h2>
          <p className="text-[#7a6455] text-[1.0625rem] mb-8">商品可能已下架或連結有誤，請回商品列表瀏覽其他商品。</p>
          <Link to="/shop" className="px-8 py-3.5 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-semibold hover:bg-[#5a3f30] transition-colors whitespace-nowrap cursor-pointer">
            回商品列表
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedProducts = product.relatedIds
    .map(rid => featuredProducts.find(p => p.id === rid))
    .filter(Boolean) as typeof featuredProducts;

  const tabs = [
    { id: "description", label: "商品介紹" },
    { id: "size", label: "尺寸說明" },
    { id: "material", label: "材質與洗滌" },
    { id: "styling", label: "穿搭建議" },
  ];

  const discount = Math.round((1 - product.salePrice / product.originalPrice) * 100);

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto pt-6 pb-2">
          <nav className="flex items-center gap-2 text-[0.9375rem] text-[#a09080]">
            <Link to="/" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">首頁</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">商品分類</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-[#3d2b1f] transition-colors cursor-pointer">{product.category}</Link>
            <span>/</span>
            <span className="text-[#3d2b1f]">{product.name}</span>
          </nav>
        </div>

        {/* Product Detail */}
        <section className="py-8 md:py-14">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 xl:gap-16">
              {/* Images */}
              <div className="w-full lg:w-[52%]">
                <div className="flex flex-col-reverse sm:flex-row gap-4">
                  {/* Thumbnails */}
                  <div className="flex sm:flex-col gap-3 sm:w-20">
                    {product.images.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedImage(idx)}
                        className={`flex-shrink-0 w-16 h-20 sm:w-20 sm:h-24 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${selectedImage === idx ? "border-[#8B5E52]" : "border-transparent opacity-60 hover:opacity-100"}`}
                      >
                        <img src={img} alt={`商品圖 ${idx + 1}`} className="w-full h-full object-cover object-top" />
                      </button>
                    ))}
                  </div>
                  {/* Main Image */}
                  <div className="flex-1 aspect-[3/4] rounded-lg overflow-hidden bg-white">
                    <img
                      src={product.images[selectedImage]}
                      alt={product.name}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="w-full lg:w-[48%] flex flex-col">
                {/* Badge & Category */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-[#f5ede4] text-[#8B5E52] text-[0.875rem] rounded-full font-medium">{product.category}</span>
                  <span className={`px-3 py-1 rounded text-[0.875rem] font-semibold ${product.badge === "新品" ? "bg-[#8B5E52] text-white" : product.badge === "熱銷" ? "bg-[#3d2b1f] text-[#f5e8d8]" : "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                    {product.badge}
                  </span>
                </div>

                {/* Name */}
                <h1 className="text-[#3d2b1f] text-[1.875rem] md:text-[2.125rem] font-bold leading-[1.35] mb-2">{product.name}</h1>
                <p className="text-[#8B5E52] text-[1.125rem] font-medium mb-6">{product.tagline}</p>

                {/* Price */}
                <div className="flex items-end gap-4 mb-6 pb-6 border-b border-[#ede8e0]">
                  <span className="text-[#8B5E52] text-[2rem] font-bold leading-none">NT${product.salePrice.toLocaleString()}</span>
                  <span className="text-[#a09080] text-[1.125rem] line-through leading-none">NT${product.originalPrice.toLocaleString()}</span>
                  <span className="px-2.5 py-1 bg-[#8B5E52]/10 text-[#8B5E52] text-[0.875rem] font-semibold rounded">省 {discount}%</span>
                </div>

                {/* Highlights */}
                <div className="mb-6">
                  <p className="text-[#3d2b1f] text-[1.0625rem] font-semibold mb-3">商品特色</p>
                  <ul className="space-y-2">
                    {product.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-[#7a6455] text-[1.0625rem] leading-[1.75]">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-checkbox-circle-line text-[#c9a98a] text-[1.125rem]"></i>
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Size Selector */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[#3d2b1f] text-[1.0625rem] font-semibold">選擇尺寸</p>
                    <button
                      onClick={() => setActiveTab("size")}
                      className="text-[#8B5E52] text-[0.9375rem] underline cursor-pointer hover:text-[#3d2b1f] transition-colors"
                    >
                      尺寸對照表
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {product.sizes.map(s => (
                      <button
                        key={s.label}
                        onClick={() => setSelectedSize(s.label)}
                        className={`w-12 h-12 rounded-md text-[1rem] font-semibold border-2 transition-all cursor-pointer ${selectedSize === s.label ? "border-[#3d2b1f] bg-[#3d2b1f] text-[#faf8f5]" : "border-[#e0d4c8] text-[#3d2b1f] hover:border-[#8B5E52]"}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {selectedSize && (
                    <p className="mt-2 text-[#8B5E52] text-[0.9375rem]">
                      已選擇：<strong>{selectedSize}</strong>
                      {product.sizes.find(s => s.label === selectedSize)?.waist !== "-" && (
                        <span className="ml-2 text-[#a09080]">
                          （胸圍 {product.sizes.find(s => s.label === selectedSize)?.chest}cm・腰圍 {product.sizes.find(s => s.label === selectedSize)?.waist}cm）
                        </span>
                      )}
                    </p>
                  )}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <a
                    href="https://line.me"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2.5 px-6 py-4 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#5a3f30] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-line-fill text-[1.25rem]"></i>
                    LINE 立即詢問
                  </a>
                  <Link
                    to="/contact"
                    className="flex-1 flex items-center justify-center gap-2.5 px-6 py-4 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#f5ede4] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-calendar-line text-[1.25rem]"></i>
                    預約試穿
                  </Link>
                </div>

                {/* Store Info */}
                <div className="p-4 bg-[#f5ede4] rounded-lg flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-store-2-line text-[#8B5E52] text-[1.125rem]"></i>
                  </span>
                  <div>
                    <p className="text-[#3d2b1f] text-[0.9375rem] font-semibold mb-0.5">台中實體門市</p>
                    <p className="text-[#7a6455] text-[0.9375rem] leading-[1.6]">
                      可至門市親自試穿，確認版型與質感後再決定。<br />
                      歡迎加入 LINE 詢問庫存與尺寸，或<Link to="/contact" className="text-[#8B5E52] underline cursor-pointer">預約試穿時間</Link>。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Detail Tabs */}
        <section className="py-12 md:py-16 bg-white">
          <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
            {/* Tab Nav */}
            <div className="flex gap-1 border-b border-[#ede8e0] mb-10 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-3.5 text-[1.0625rem] font-semibold whitespace-nowrap cursor-pointer transition-all border-b-2 ${activeTab === tab.id ? "text-[#3d2b1f] border-[#3d2b1f]" : "text-[#a09080] border-transparent hover:text-[#7a6455]"}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="max-w-[800px]">
              {activeTab === "description" && (
                <div>
                  <p className="text-[#7a6455] text-[1.1875rem] leading-[1.75] mb-8">{product.description}</p>
                  <h4 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-4">
                    <a id="product-highlights" className="text-[#3d2b1f]">商品特色說明</a>
                  </h4>
                  <ul className="space-y-3">
                    {product.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#7a6455] text-[1.0625rem] leading-[1.75]">
                        <span className="w-6 h-6 flex items-center justify-center flex-shrink-0 bg-[#f5ede4] rounded-full mt-0.5">
                          <strong className="text-[#8B5E52] text-[0.8125rem]">{i + 1}</strong>
                        </span>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeTab === "size" && (
                <div>
                  <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] mb-6">{product.fitDescription}</p>
                  <h4 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-4">
                    <a id="size-chart" className="text-[#3d2b1f]">尺寸對照表</a>
                  </h4>
                  <p className="text-[#a09080] text-[0.9375rem] mb-4">尺寸單位：公分（cm）</p>
                  <div className="overflow-x-auto rounded-lg border border-[#ede8e0]">
                    <table className="w-full text-[1rem]">
                      <thead>
                        <tr className="bg-[#f5ede4]">
                          <th className="px-5 py-3.5 text-left text-[#3d2b1f] font-semibold">尺寸</th>
                          <th className="px-5 py-3.5 text-center text-[#3d2b1f] font-semibold">胸圍</th>
                          <th className="px-5 py-3.5 text-center text-[#3d2b1f] font-semibold">腰圍</th>
                          <th className="px-5 py-3.5 text-center text-[#3d2b1f] font-semibold">臀圍</th>
                          <th className="px-5 py-3.5 text-center text-[#3d2b1f] font-semibold">衣長/裙長</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.sizes.map((s, i) => (
                          <tr key={s.label} className={i % 2 === 0 ? "bg-white" : "bg-[#faf8f5]"}>
                            <td className="px-5 py-3.5 font-bold text-[#3d2b1f]">{s.label}</td>
                            <td className="px-5 py-3.5 text-center text-[#7a6455]">{s.chest}</td>
                            <td className="px-5 py-3.5 text-center text-[#7a6455]">{s.waist}</td>
                            <td className="px-5 py-3.5 text-center text-[#7a6455]">{s.hip}</td>
                            <td className="px-5 py-3.5 text-center text-[#7a6455]">{s.length}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-4 text-[#a09080] text-[0.9375rem]">以上尺寸為商品實際尺寸，非人體尺寸。建議加上 2-3cm 的穿著餘裕量。</p>
                  <div className="mt-6 p-4 bg-[#f5ede4] rounded-lg">
                    <p className="text-[#3d2b1f] text-[1rem] font-semibold mb-1.5">不確定選哪個尺寸？</p>
                    <p className="text-[#7a6455] text-[1rem] leading-[1.6]">
                      歡迎加入 LINE 告知您的三圍，由我們的專業造型顧問為您推薦最適合的尺寸。
                    </p>
                    <a
                      href="https://line.me"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-[#8B5E52] text-[1rem] font-semibold hover:text-[#3d2b1f] transition-colors cursor-pointer"
                    >
                      <i className="ri-line-fill"></i> 加入 LINE 詢問尺寸
                    </a>
                  </div>
                </div>
              )}

              {activeTab === "material" && (
                <div>
                  <h4 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-4">
                    <a id="material-info" className="text-[#3d2b1f]">材質說明</a>
                  </h4>
                  <div className="p-5 bg-[#faf8f5] rounded-lg mb-8">
                    <p className="text-[#7a6455] text-[1.0625rem] leading-[1.9] whitespace-pre-line">{product.material}</p>
                  </div>
                  <h4 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-4">
                    <a id="care-instructions" className="text-[#3d2b1f]">洗滌與保養</a>
                  </h4>
                  <ul className="space-y-3">
                    {product.careInstructions.map((c, i) => (
                      <li key={i} className="flex items-start gap-3 text-[#7a6455] text-[1.0625rem] leading-[1.75]">
                        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-information-line text-[#c9a98a] text-[1.125rem]"></i>
                        </span>
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {activeTab === "styling" && (
                <div>
                  <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] mb-6">
                    以下是我們的造型顧問針對此款商品整理的穿搭搭配建議，讓您更輕鬆地將這件單品融入日常衣橱。
                  </p>
                  <h4 className="text-[#3d2b1f] text-[1.25rem] font-bold mb-4">
                    <a id="styling-tips" className="text-[#3d2b1f]">造型搭配建議</a>
                  </h4>
                  <ul className="space-y-4">
                    {product.stylingTips.map((tip, i) => (
                      <li key={i} className="flex items-start gap-4 p-4 bg-[#faf8f5] rounded-lg">
                        <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 bg-[#8B5E52] rounded-full">
                          <strong className="text-white text-[0.875rem]">{i + 1}</strong>
                        </span>
                        <p className="text-[#7a6455] text-[1.0625rem] leading-[1.75] pt-0.5">{tip}</p>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 p-5 bg-[#3d2b1f] rounded-lg text-center">
                    <p className="text-[#f5e8d8] text-[1.0625rem] leading-[1.75] mb-4">
                      想了解更多穿搭靈感？瀏覽我們的穿搭提案頁面，或到門市讓造型顧問親自為您搭配。
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <Link to="/lookbook" className="px-6 py-2.5 bg-[#c9a98a] text-[#3d2b1f] rounded-full text-[1rem] font-bold hover:bg-[#b8957a] transition-colors cursor-pointer whitespace-nowrap">
                        瀏覽穿搭提案
                      </Link>
                      <Link to="/contact" className="px-6 py-2.5 border-2 border-[#c9a98a] text-[#c9a98a] rounded-full text-[1rem] font-semibold hover:bg-[#c9a98a] hover:text-[#3d2b1f] transition-colors cursor-pointer whitespace-nowrap">
                        預約試穿
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="py-14 md:py-20 bg-[#faf8f5]">
            <div className="w-full px-6 md:px-16 max-w-[1400px] mx-auto">
              <div className="text-center mb-10">
                <p className="text-[#8B5E52] text-[1rem] tracking-[0.25em] uppercase font-medium mb-2">You May Also Like</p>
                <h2 className="text-[#3d2b1f] text-[1.75rem] md:text-[2.125rem] font-bold">相關商品推薦</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-product-shop>
                {relatedProducts.map(p => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className="group bg-white rounded-lg overflow-hidden cursor-pointer block"
                  >
                    <div className="relative aspect-[5/7] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className={`absolute top-3 left-3 px-2.5 py-1 rounded text-[0.875rem] font-semibold ${p.badge === "新品" ? "bg-[#8B5E52] text-white" : p.badge === "熱銷" ? "bg-[#3d2b1f] text-[#f5e8d8]" : "bg-[#c9a98a] text-[#3d2b1f]"}`}>
                        {p.badge}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="text-[#7a6455] text-[0.875rem] mb-1">{p.category}</p>
                      <h3 className="text-[#3d2b1f] text-[1.0625rem] font-semibold leading-[1.45] mb-2">{p.name}</h3>
                      <div className="flex items-center gap-2">
                        <span className="text-[#8B5E52] text-[1.0625rem] font-bold">NT${p.salePrice.toLocaleString()}</span>
                        <span className="text-[#a09080] text-[0.875rem] line-through">NT${p.originalPrice.toLocaleString()}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#3d2b1f] hover:text-[#faf8f5] transition-colors cursor-pointer whitespace-nowrap">
                  瀏覽全部商品 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* LINE CTA */}
        <section className="py-14 bg-[#f5ede4] text-center px-6">
          <p className="text-[#8B5E52] text-[1rem] tracking-[0.25em] uppercase font-medium mb-3">Any Questions?</p>
          <h2 className="text-[#3d2b1f] text-[1.75rem] md:text-[2.125rem] font-bold mb-4">有任何商品或尺寸問題？</h2>
          <p className="text-[#7a6455] text-[1.1875rem] mb-8 max-w-lg mx-auto leading-[1.75]">
            歡迎加入 LINE 與我們的造型顧問即時溝通，提供您最即時、最貼心的購物建議。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://line.me" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-3.5 bg-[#3d2b1f] text-[#faf8f5] rounded-full text-[1.0625rem] font-bold hover:bg-[#5a3f30] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-line-fill"></i> 加入 LINE 諮詢
            </a>
            <Link to="/contact"
              className="flex items-center gap-2 px-8 py-3.5 border-2 border-[#3d2b1f] text-[#3d2b1f] rounded-full text-[1.0625rem] font-semibold hover:bg-[#e8d8c8] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-calendar-2-line"></i> 預約試穿
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
